// Subtaken in de Flow-takenlijst (planning-gauntlet stap 4, 25 sep 2026): pure logica.
//
// De engine levert per taak `parentTaskId` en `subtaskIds` naast `fields` (plus
// fields["Parent task"] en fields["Sub tasks"]). Een oudere engine levert die niet: dan
// is er geen enkele ouder bekend en blijft alles plat — zonder fouten.

const lijst = (v) => (Array.isArray(v) ? v : (v === undefined || v === null || v === '' ? [] : [v]));

/** Id van de ouder, of null. Topniveau `parentTaskId` wint (ook als hij expliciet null is). */
export function ouderIdVan(taak) {
  if (!taak) return null;
  if (Object.prototype.hasOwnProperty.call(taak, 'parentTaskId')) return taak.parentTaskId || null;
  return lijst(taak.fields?.['Parent task'])[0] || null;
}

/** Subtaak-ids van een ouder in sjabloonvolgorde (topniveau eerst, dan het veld). */
export function subIdsVan(taak) {
  const uit = [];
  [...lijst(taak?.subtaskIds), ...lijst(taak?.fields?.['Sub tasks'])].forEach((i) => {
    const s = String(i || '');
    if (s && !uit.includes(s)) uit.push(s);
  });
  return uit;
}

/** Levert de engine de subtaakvelden? Alleen dan tonen we "+ subtaak" (anders zou een oude
 * engine het veld negeren en een losse taak maken). */
export const kentSubtaken = (taken) => (taken || []).some((t) => t && (
  Object.prototype.hasOwnProperty.call(t, 'parentTaskId') || Object.prototype.hasOwnProperty.call(t, 'subtaskIds')));

/** kind-id → ouder-id, uit beide richtingen (een oud record kent alleen `Sub tasks` op de ouder). */
export function ouderKaart(taken) {
  const kaart = new Map();
  const ids = new Set((taken || []).map((t) => t.id));
  (taken || []).forEach((t) => {
    const o = ouderIdVan(t);
    if (o && o !== t.id) kaart.set(t.id, o);
  });
  (taken || []).forEach((ouder) => {
    subIdsVan(ouder).forEach((k) => {
      const kind = (taken || []).find((t) => t.id === k);
      // Alleen als het kind zelf geen (andere) ouder noemt: parentTaskId is leidend.
      if (ids.has(k) && k !== ouder.id && !kaart.has(k) && !(kind && Object.prototype.hasOwnProperty.call(kind, 'parentTaskId'))) {
        kaart.set(k, ouder.id);
      }
    });
  });
  return kaart;
}

const datumVan = (t) => String(t.fields?.StartDateUitvoerder || t.fields?.EndDateUitvoerder || t.fields?.Deadline || '9999');

/**
 * Taken van één groep als boom: `[{ taak, kinderen }]`. Een taak waarvan de ouder óók in de
 * groep staat, hangt onder die ouder; staat de ouder er niet (andere fase, al af), dan blijft
 * hij gewoon los in de lijst. Kinderen komen uit `alle` (ook afgeronde, doorgestreept),
 * in de volgorde van de ouder, dan op datum.
 */
export function bouwBoom(groepTaken, alle, kaart = ouderKaart(alle)) {
  const inGroep = new Set((groepTaken || []).map((t) => t.id));
  const top = (groepTaken || []).filter((t) => !(kaart.has(t.id) && inGroep.has(kaart.get(t.id))));
  return top.map((taak) => {
    const volgorde = subIdsVan(taak);
    const rang = (k) => { const i = volgorde.indexOf(k.id); return i < 0 ? volgorde.length : i; };
    const kinderen = (alle || [])
      .filter((k) => kaart.get(k.id) === taak.id)
      .sort((a, b) => rang(a) - rang(b) || datumVan(a).localeCompare(datumVan(b)));
    return { taak, kinderen };
  });
}

/** Afgeronde taken die al onder een open ouder staan, horen niet nóg eens in "afgeronde taken". */
export function losAfgerond(klaar, open, kaart) {
  const openIds = new Set((open || []).map((t) => t.id));
  return (klaar || []).filter((t) => !(kaart.has(t.id) && openIds.has(kaart.get(t.id))));
}

// ── lokale staat bijwerken (ProjectDataContext) ──

/** Nieuwe taak erbij; is het een subtaak, dan komt hij ook in `subtaskIds` van de ouder. */
export function metNieuweTaak(taken, record) {
  const ouder = ouderIdVan(record);
  const bij = (taken || []).map((t) => (ouder && t.id === ouder
    ? { ...t, subtaskIds: [...subIdsVan(t).filter((i) => i !== record.id), record.id] }
    : t));
  return [...bij, record];
}

/** Taak weg (ongedaan maken van toevoegen); ook uit de lijst van zijn ouder. */
export function zonderTaak(taken, id) {
  return (taken || []).filter((t) => t.id !== id).map((t) => (subIdsVan(t).includes(id)
    ? { ...t, subtaskIds: subIdsVan(t).filter((i) => i !== id), fields: { ...t.fields, 'Sub tasks': lijst(t.fields?.['Sub tasks']).filter((i) => i !== id) } }
    : t));
}

/** Ouder van `id` wijzigen (losmaken = `nieuw` null). */
export function metOuder(taken, id, nieuw) {
  return (taken || []).map((t) => {
    if (t.id === id) {
      return { ...t, parentTaskId: nieuw || null, fields: { ...t.fields, 'Parent task': nieuw ? [nieuw] : [] } };
    }
    const ids = subIdsVan(t).filter((i) => i !== id);
    if (nieuw && t.id === nieuw) return { ...t, subtaskIds: [...ids, id] };
    if (subIdsVan(t).includes(id)) {
      return { ...t, subtaskIds: ids, fields: { ...t.fields, 'Sub tasks': lijst(t.fields?.['Sub tasks']).filter((i) => i !== id) } };
    }
    return t;
  });
}

/** Type voor "+ taak toevoegen" in een groep: dat van de groep als het bij de fase hoort. */
export function typeVoorGroep(groepTaken, faseTypes) {
  const types = faseTypes || [];
  const eigen = (groepTaken || []).map((t) => t.fields?.Type).find((ty) => ty && types.includes(ty));
  return eigen || types[0] || undefined;
}

/** POST-body voor snel toevoegen in de Flow. Airtable-veldnamen: werkt ook op een oude engine. */
export function nieuweFlowTaak({ naam, projectId, deliverableId, type, parentTaskId }) {
  return {
    NaamTaak: String(naam || '').trim(),
    ...(projectId ? { Project: [projectId] } : {}),
    ...(deliverableId ? { Deliverable: [deliverableId] } : {}),
    ...(type ? { Type: type } : {}),
    ...(parentTaskId ? { parentTaskId } : {}),
  };
}
